// External libraries
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Own libraries
import interpolate from '@eventbrite/interpolate-lazy-string';
import { ToggleButton } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import {
    ChevronDownChunky,
    ChevronUpChunky,
} from '@eventbrite/eds-iconography';

// Local imports
import VariantListItem from './VariantListItem';
import {
    getVariantRangeCostForDisplay,
    getVariantCostForDisplay,
    getVariantDiscountCostForDisplay,
} from '../utils/ticketPriceUtils';
import {
    isTierVariantBestAvailable,
    isAddOnTicket,
} from '../utils/ticketUtils';
import { determineTieredOnsaleStatusDisplayOptions } from '../utils/ticketTierUtils';
import TierPile from '../../../components/reserved/TierPile';
import { TierColorCircle } from '../../../components/tierColorCircle/TierColorCircle';
import { SMALL_COLOR_CIRCLE_SIZE } from '../../../components/tierColorCircle/constants';
import { PROMO_CODE_OPTIONS_PROP_TYPE } from '../../../components/promoCode/constants';
import {
    VARIANT_SHAPE,
    APPLIED_PROMO_MESSAGING,
    PROMO_CODE,
    PERCENT_OFF,
    AMOUNT_OFF,
    ALL_VARIANTS,
    ONE_VARIANT,
    SOME_VARIANTS,
} from '../../../constants';
import { gettext } from '@eventbrite/i18n';
import { TIER_DEFAULT_COLOR } from '../constants';

const _numVariantsWithPromoApplied = (variants) =>
    variants.reduce((acc, val) => (val.code ? acc + 1 : acc), 0);
const _promoCodeAppliedString = (promoCodeFormOptions, variants) => {
    let message;

    if (promoCodeFormOptions.promoCode) {
        const { promoCode, percentOff, amountOff } = promoCodeFormOptions;
        const numOptions = _numVariantsWithPromoApplied(variants);
        let messageKey;

        if (percentOff) {
            messageKey = PERCENT_OFF;
        } else if (amountOff) {
            messageKey = AMOUNT_OFF;
        } else {
            messageKey = PROMO_CODE;
        }

        if (numOptions > 0) {
            // if neither percentOff nor amountOff are set, it is non discounted access code or hold
            const appliedPromoMessaging = APPLIED_PROMO_MESSAGING[messageKey];

            if (numOptions >= variants.length - 1) {
                message = appliedPromoMessaging[ALL_VARIANTS];
            } else if (numOptions === 1) {
                message = appliedPromoMessaging[ONE_VARIANT];
            } else {
                message = appliedPromoMessaging[SOME_VARIANTS];
            }

            message = (
                <div
                    className="eds-text-bs--fixed eds-text-color--grey-600"
                    data-spec="ticket-variant-discount-messaging"
                >
                    {interpolate(
                        message,
                        { promoCode, percentOff, amountOff, numOptions },
                        true,
                    )}
                </div>
            );
        }
    }
    return message;
};
const getPromoAppliedCostDisplay = (selectedVariant, currencyFormat) => {
    const costDisplayValue = getVariantDiscountCostForDisplay(
        selectedVariant,
        currencyFormat,
    );

    return (
        <span className="eds-text-color--grey-500 eds-text--strikethrough eds-l-mar-left-1">
            {costDisplayValue}
        </span>
    );
};
const tierPileFactory = (variants, iconSize) => (
    <TierPile variants={variants} iconSize={iconSize} />
);
const tierCircleFactory = (color, circleSize) => (
    <TierColorCircle
        tierColor={color}
        colorCircleSize={circleSize}
        customClassNames="eds-l-mar-right-4"
    />
);

export const getDropdownButtonIcon = (variant, variants) => {
    if (!variant || isAddOnTicket(variant)) {
        return null;
    }

    return variant.primary
        ? tierPileFactory(variants, SMALL_COLOR_CIRCLE_SIZE)
        : tierCircleFactory(variant.color, SMALL_COLOR_CIRCLE_SIZE);
};

export const getDropdownButtonLabel = (variant, variants, currencyFormat) => {
    // Case for Add-on with nested variants
    if (!variant) {
        const option = getVariantRangeCostForDisplay(variants, currencyFormat);

        return gettext('Choose option - %(option)s', { option });
    }

    // Single level Add-on
    if (isAddOnTicket(variant)) {
        return `${variant.name} - ${getVariantCostForDisplay(
            variant,
            currencyFormat,
        )}`;
    }

    // Best Available for tiered admission ticket
    if (variant.primary) {
        return getVariantRangeCostForDisplay(variants, currencyFormat);
    }

    // Single tier for tiered admission ticket
    return getVariantCostForDisplay(variant, currencyFormat);
};

export default class SelectTicketVariant extends React.PureComponent {
    static propTypes = {
        /** A function that will be called upon selecting a variant. The argument is the variant ID. */
        handleChangeVariant: PropTypes.func,
        /** The id of the currently selected variant */
        selectedVariant: VARIANT_SHAPE,
        /** Array of variants with props `id`, `color`, `name`, and `primary`. Expects one variant to have `primary` set to true. */
        variants: PropTypes.arrayOf(VARIANT_SHAPE),
        /** PromoCodeFormOptions for displaying % / amount off if applicable */
        promoCodeFormOptions: PROMO_CODE_OPTIONS_PROP_TYPE,
    };

    static defaultProps = {
        promoCodeFormOptions: {},
    };

    state = {
        isExpanded: false,
    };

    _handleOnPress = () => {
        this.setState((state) => ({ isExpanded: !state.isExpanded }));
    };

    _handleSelect = (variant) => () => {
        this.props.handleChangeVariant(variant);
        this.setState((state) => ({ isExpanded: !state.isExpanded }));
    };

    render() {
        const {
            currencyFormat,
            selectedVariant = { color: TIER_DEFAULT_COLOR },
            variants = [],
            promoCodeFormOptions,
            locale,
            isFormSubmitting,
            isEventWithShippingFee,
        } = this.props;
        const { isExpanded } = this.state;
        const bestAvailablePriceRange = getVariantRangeCostForDisplay(
            variants,
            currencyFormat,
        );

        const tierToggleClasses = classNames(
            'eds-align--left',
            'eds-align--center-vertical',
            'select-ticket-variant__details',
        );

        const variantListItems = variants.map((v, i) => {
            const isBestAvailable = isTierVariantBestAvailable(v);
            const variantCost = isBestAvailable
                ? bestAvailablePriceRange
                : getVariantCostForDisplay(v, currencyFormat);
            const isSelected = selectedVariant
                ? v.id === selectedVariant.id
                : false;
            const extraContent = getDropdownButtonIcon(v, variants);
            const discountedCost = getPromoAppliedCostDisplay(
                v,
                currencyFormat,
            );
            const displayName = isBestAvailable
                ? gettext('Best Available')
                : v.name;
            const statusDisplayOptions =
                determineTieredOnsaleStatusDisplayOptions(v, locale);

            return (
                <VariantListItem
                    isSelected={isSelected}
                    statusDisplayOptions={statusDisplayOptions}
                    displayName={displayName}
                    key={v.id}
                    id={v.id}
                    onSelect={this._handleSelect(v)}
                    showDivider={i < variants.length - 1}
                    discountedCost={discountedCost}
                    variantCost={variantCost}
                    extraContent={extraContent}
                    isFormSubmitting={isFormSubmitting}
                    isEventWithShippingFee={isEventWithShippingFee}
                    feeAndTax={v.feeAndTax}
                />
            );
        });

        const variantList = this.state.isExpanded ? (
            <ul
                className="select-ticket-variant__list"
                data-spec="select-ticket-variant-list"
            >
                {variantListItems}
            </ul>
        ) : null;
        const dropdownButtonIcon = getDropdownButtonIcon(
            selectedVariant,
            variants,
        );
        const dropdownButtonLabel = getDropdownButtonLabel(
            selectedVariant,
            variants,
            currencyFormat,
        );

        return (
            <div
                className="select-ticket-variant eds-l-mar-bot-4"
                data-spec="select-ticket-expansion-panel"
            >
                <div className={tierToggleClasses}>
                    {dropdownButtonIcon}
                    <div>
                        <ToggleButton
                            style="none"
                            className="eds-l-mar-bot-2"
                            onPress={this._handleOnPress}
                        >
                            <div
                                className="eds-link eds-text-bm"
                                data-spec="selected-ticket-variant-cost"
                            >
                                {dropdownButtonLabel}
                                {getPromoAppliedCostDisplay(selectedVariant)}
                            </div>
                        </ToggleButton>
                        <ToggleButton
                            style="none"
                            childrenAlt={<Icon type={<ChevronUpChunky />} />}
                            isPressed={isExpanded}
                            onPress={this._handleOnPress}
                        >
                            <Icon type={<ChevronDownChunky />} />
                        </ToggleButton>
                    </div>
                    <div>
                        {_promoCodeAppliedString(
                            promoCodeFormOptions,
                            variants,
                        )}
                    </div>
                </div>
                {variantList}
            </div>
        );
    }
}
